import { useMemo, useCallback } from 'react';
import { type FlagConfiguration, useFlagsService } from '@atlassian/jira-flags';
import type { FlagData } from '../../../common/ui/types';
import messages from './messages';

export const usePrioritySchemeShowFlags = () => {
	const { showFlag } = useFlagsService();

	const getActions = useCallback(
		(schemeName: string) => ({
			actions: [
				{
					content: messages.viewPrioritySchemesAction,
					href: `/jira/settings/issues/priority-schemes?schemeName=${schemeName}`,
				},
			],
		}),
		[],
	);

	const createSchemeSuccessFlag = useMemo(
		() =>
			({ schemeName, schemeId }: FlagData): FlagConfiguration => ({
				type: 'success',
				id: `priority-scheme-create-success-flag-${schemeId}`,
				testId: 'admin-pages-priority-schemes-priority-scheme-modal.create-scheme-flag',
				title: [messages.createdSchemeTitle, { name: schemeName }],
				isAutoDismiss: true,
				...getActions(schemeName),
			}),
		[getActions],
	);

	const editSchemeSuccessFlag = ({ schemeName, schemeId }: FlagData): FlagConfiguration => ({
		type: 'success',
		id: `priority-scheme-edit-success-flag-${schemeId}`,
		testId: 'admin-pages-priority-schemes-priority-scheme-modal.edit-scheme-flag',
		title: messages.editedSchemeTitle,
		description: [messages.editedSchemeDescription, { name: schemeName }],
		isAutoDismiss: true,
		...getActions(schemeName),
	});

	const createSchemeAsyncSuccessFlag = ({ schemeId }: FlagData): FlagConfiguration => ({
		type: 'success',
		id: `priority-scheme-async-create-flag-${schemeId}`,
		testId: 'admin-pages-priority-schemes-priority-scheme-modal.create-scheme-async-flag',
		title: messages.longRequestCreateTitle,
		description: messages.longRequestDescription,
		isAutoDismiss: true,
	});

	const editSchemeAsyncSuccessFlag = ({ schemeId }: FlagData): FlagConfiguration => ({
		type: 'success',
		id: `priority-scheme-async-edit-flag-${schemeId}`,
		testId: 'admin-pages-priority-schemes-priority-scheme-modal.edit-scheme-async-flag',
		title: messages.longRequestEditTitle,
		description: messages.longRequestDescription,
		isAutoDismiss: true,
	});

	const showSuccessFlag = (flagData: FlagData, isCreatingNewScheme: boolean) => {
		const flagConfig: FlagConfiguration = isCreatingNewScheme
			? createSchemeSuccessFlag(flagData)
			: editSchemeSuccessFlag(flagData);

		showFlag(flagConfig);
	};

	const showAsyncSuccessFlag = (flagData: FlagData, isCreatingNewScheme: boolean) => {
		const flagConfig: FlagConfiguration = isCreatingNewScheme
			? createSchemeAsyncSuccessFlag(flagData)
			: editSchemeAsyncSuccessFlag(flagData);

		showFlag(flagConfig);
	};

	const showUnexpectedErrorFlag = (isCreatingNewScheme: boolean) => {
		const flagConfig: FlagConfiguration = {
			type: 'error',
			id: 'priority-scheme-unexpected-error-flag',
			testId: 'admin-pages-priority-schemes-priority-scheme-modal.unexpected-error-flag',
			title: isCreatingNewScheme
				? messages.unexpectedCreateErrorTitle
				: messages.unexpectedEditErrorTitle,
			description: messages.unexpectedErrorBodyMessage,
			isAutoDismiss: true,
		};

		showFlag(flagConfig);
	};

	return {
		showSuccessFlag,
		showAsyncSuccessFlag,
		showUnexpectedErrorFlag,
	};
};
